/**
 * Owner cookbook management (P2-F2 sharing & invites) — `/cookbooks/:cookbookId/manage`.
 *
 * Client-gated on the TARGET cookbook's role being `owner` (a UX hint only):
 * anyone else who navigates here — a non-member, or a member with a weaker
 * role — is redirected back to the feed. The API independently re-checks
 * `cookbook:manage` (owner-only) on every request this page makes; a stale/
 * tampered client can never see or change more than the server allows.
 *
 * Keeps state simple by REFETCHING the whole access list after every mutation
 * (invite / role change / revoke / cancel) rather than patching local state —
 * this keeps the member-cap count always correct without duplicating the
 * server's counting logic.
 */

import {
  MAX_COOKBOOK_MEMBERS,
  type CookbookAccessList,
  type CookbookInviteRole,
} from "@tjommi/shared";
import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import {
  BadRequestError,
  ConflictError,
  EntitlementRequiredError,
  ForbiddenError,
  UnauthorizedError,
  cancelCookbookInvite,
  fetchCookbookAccess,
  inviteCookbookMember,
  revokeCookbookMember,
  updateCookbookMemberRole,
} from "../api";
import { PremiumNotice } from "../components/PremiumNotice";
import { formatDateTime } from "../lib/formatDate";
import { useCookbook } from "../cookbook/CookbookContext";

const ROLE_LABEL: Record<CookbookInviteRole, string> = {
  write: "Skrive",
  read: "Lese",
};

type AccessState =
  | { status: "loading" }
  | { status: "ok"; data: CookbookAccessList }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

/** The email/role controls used both by the invite form and the future re-use. */
function RoleSelect({
  value,
  onChange,
  disabled,
  testId,
  ariaLabel,
}: {
  value: CookbookInviteRole;
  onChange: (role: CookbookInviteRole) => void;
  disabled?: boolean;
  testId: string;
  ariaLabel: string;
}): React.JSX.Element {
  return (
    <select
      className="input"
      aria-label={ariaLabel}
      data-testid={testId}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as CookbookInviteRole)}
    >
      <option value="write">{ROLE_LABEL.write}</option>
      <option value="read">{ROLE_LABEL.read}</option>
    </select>
  );
}

export function CookbookManagePage({
  accessToken,
  onSessionExpired,
}: {
  accessToken: string;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const { cookbookId } = useParams<{ cookbookId: string }>();
  const { cookbooks } = useCookbook();
  const target = cookbooks.find((c) => c.id === cookbookId);
  const isOwner = target?.role === "owner";

  const [access, setAccess] = useState<AccessState>({ status: "loading" });

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<CookbookInviteRole>("write");
  const [inviting, setInviting] = useState(false);
  // Either a short string (the existing inline idiom) OR the parsed 402 —
  // ONE state, so the two can never be shown at the same time or leak into
  // each other. `sharing` on Gratis is not a user mistake, so it renders as a
  // `PremiumNotice` instead of `.feed-error` prose.
  const [inviteError, setInviteError] = useState<string | EntitlementRequiredError | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const [busySubject, setBusySubject] = useState<string | null>(null);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!cookbookId || !isOwner) return;
    setAccess({ status: "loading" });
    fetchCookbookAccess(cookbookId, accessToken)
      .then((data) => setAccess({ status: "ok", data }))
      .catch((err: unknown) => {
        if (err instanceof UnauthorizedError) {
          setAccess({ status: "unauthorized" });
        } else {
          setAccess({
            status: "error",
            message: err instanceof Error ? err.message : "Ukjent feil",
          });
        }
      });
  }, [cookbookId, isOwner, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  // A non-owner (or a stale cookbookId the caller no longer belongs to) never
  // sees this screen — the API would 403/404 anyway, but this avoids a
  // confusing forbidden/empty flash.
  if (!cookbookId || !isOwner) {
    return <Navigate to="/" replace />;
  }

  const memberCount = access.status === "ok" ? access.data.members.length : 0;
  const pendingInvites = access.status === "ok" ? access.data.invites : [];
  const pendingCount = pendingInvites.filter((i) => i.status === "pending").length;
  const totalCount = memberCount + pendingCount;
  const atCap = totalCount >= MAX_COOKBOOK_MEMBERS;

  function handleInvite(e: React.FormEvent): void {
    e.preventDefault();
    if (!cookbookId || atCap) return;
    const email = inviteEmail.trim();
    if (!email) return;

    setInviteError(null);
    setInviteSuccess(null);
    setInviting(true);
    inviteCookbookMember(cookbookId, { email, role: inviteRole }, accessToken)
      .then(() => {
        setInviteEmail("");
        setInviteSuccess(`Invitasjon sendt til ${email}.`);
        load();
      })
      .catch((err: unknown) => {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        if (err instanceof EntitlementRequiredError) {
          // Sharing isn't in the plan — distinct from ConflictError ("the plan
          // allows sharing, but this cookbook is full") and from
          // ForbiddenError ("you are not the owner").
          setInviteError(err);
        } else if (err instanceof ConflictError) {
          setInviteError(`Kokeboken har nådd maks antall medlemmer (${MAX_COOKBOOK_MEMBERS}).`);
        } else if (err instanceof BadRequestError) {
          setInviteError("Ugyldig e-post.");
        } else if (err instanceof ForbiddenError) {
          setInviteError("Ingen tilgang.");
        } else {
          setInviteError(err instanceof Error ? err.message : "Kunne ikke sende invitasjon.");
        }
      })
      .finally(() => setInviting(false));
  }

  function handleRoleChange(subject: string, role: CookbookInviteRole): void {
    setRowError(null);
    setBusySubject(subject);
    updateCookbookMemberRole(cookbookId!, subject, role, accessToken)
      .then(() => load())
      .catch((err: unknown) => {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setRowError(err instanceof Error ? err.message : "Kunne ikke endre rolle.");
      })
      .finally(() => setBusySubject(null));
  }

  function handleRevoke(subject: string, label: string): void {
    if (!window.confirm(`Fjerne ${label} fra kokeboken?`)) return;
    setRowError(null);
    setBusySubject(subject);
    revokeCookbookMember(cookbookId!, subject, accessToken)
      .then(() => load())
      .catch((err: unknown) => {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setRowError(err instanceof Error ? err.message : "Kunne ikke fjerne medlemmet.");
      })
      .finally(() => setBusySubject(null));
  }

  function handleCancelInvite(inviteId: string): void {
    setRowError(null);
    setBusyInviteId(inviteId);
    cancelCookbookInvite(cookbookId!, inviteId, accessToken)
      .then(() => load())
      .catch((err: unknown) => {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setRowError(err instanceof Error ? err.message : "Kunne ikke avbryte invitasjonen.");
      })
      .finally(() => setBusyInviteId(null));
  }

  return (
    <main className="app-main" data-testid="manage-page">
      <Link to="/" className="link-btn" data-testid="manage-back">
        ← Tilbake til oppskrifter
      </Link>

      <div className="feed-h" style={{ marginTop: "16px" }}>
        <h1 data-testid="manage-title">Del «{target?.name}»</h1>
        {access.status === "ok" && (
          <span className="feed-count" data-testid="manage-cap">
            {totalCount} av {MAX_COOKBOOK_MEMBERS} medlemmer
          </span>
        )}
      </div>

      {access.status === "loading" && (
        <div className="feed-state" data-testid="manage-loading">
          <p>Laster …</p>
        </div>
      )}

      {access.status === "unauthorized" && (
        <div className="feed-state feed-error" data-testid="manage-unauthorized">
          <h2>Økten er utløpt</h2>
          <p>Du må logge inn på nytt.</p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: "16px" }}
            data-testid="manage-relogin"
            onClick={onSessionExpired}
          >
            Logg inn igjen
          </button>
        </div>
      )}

      {access.status === "error" && (
        <div className="feed-state feed-error" data-testid="manage-error">
          <h2>Kunne ikke laste medlemmer</h2>
          <p>{access.message}</p>
        </div>
      )}

      {access.status === "ok" && (
        <>
          <section className="card card-pad manage-section" data-testid="invite-section">
            <h2 className="detail-h">Inviter noen</h2>
            <form className="invite-form" onSubmit={handleInvite}>
              <input
                type="email"
                className="input"
                placeholder="E-postadresse"
                aria-label="E-postadresse"
                data-testid="invite-email"
                value={inviteEmail}
                disabled={atCap || inviting}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
              <RoleSelect
                value={inviteRole}
                onChange={setInviteRole}
                disabled={atCap || inviting}
                testId="invite-role"
                ariaLabel="Rolle for invitasjonen"
              />
              <button
                type="submit"
                className="btn btn-primary"
                data-testid="invite-submit"
                disabled={atCap || inviting || !inviteEmail.trim()}
              >
                {inviting ? "Inviterer …" : "Inviter"}
              </button>
            </form>
            {atCap && (
              <p className="invite-hint" data-testid="invite-cap-reached">
                Kokeboken har nådd maks antall medlemmer ({MAX_COOKBOOK_MEMBERS}).
              </p>
            )}
            {inviteError instanceof EntitlementRequiredError ? (
              <PremiumNotice denial={inviteError} testId="invite-premium" />
            ) : inviteError ? (
              <p className="feed-error" data-testid="invite-error">
                {inviteError}
              </p>
            ) : null}
            {inviteSuccess && (
              <p className="invite-success" data-testid="invite-success">
                {inviteSuccess}
              </p>
            )}
          </section>

          {rowError && (
            <p className="feed-error" data-testid="manage-row-error" style={{ marginTop: "16px" }}>
              {rowError}
            </p>
          )}

          <section className="manage-section" data-testid="members-section">
            <h2 className="detail-h">Medlemmer</h2>
            <ul className="member-list" data-testid="members-list">
              {access.data.members.map((member) => {
                const label = member.displayName ?? member.email ?? member.subject;
                const isMemberOwner = member.role === "owner";
                const busy = busySubject === member.subject;
                return (
                  <li
                    key={member.subject}
                    className="member-row card card-pad"
                    data-testid={`member-row-${member.subject}`}
                  >
                    <div className="member-body">
                      <div className="member-name" data-testid={`member-name-${member.subject}`}>
                        {label}
                      </div>
                      {member.email && (
                        <div
                          className="member-email"
                          data-testid={`member-email-${member.subject}`}
                        >
                          {member.email}
                        </div>
                      )}
                    </div>
                    {isMemberOwner ? (
                      <span
                        className="chip clay"
                        data-testid={`member-role-label-${member.subject}`}
                      >
                        Eier
                      </span>
                    ) : (
                      <div className="member-actions">
                        <RoleSelect
                          value={member.role as CookbookInviteRole}
                          onChange={(role) => handleRoleChange(member.subject, role)}
                          disabled={busy}
                          testId={`member-role-${member.subject}`}
                          ariaLabel={`Rolle for ${label}`}
                        />
                        <button
                          type="button"
                          className="btn btn-danger"
                          data-testid={`member-remove-${member.subject}`}
                          disabled={busy}
                          onClick={() => handleRevoke(member.subject, label)}
                        >
                          Fjern tilgang
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="manage-section" data-testid="invites-section">
            <h2 className="detail-h">Ventende invitasjoner</h2>
            {pendingInvites.length === 0 ? (
              <p className="invites-empty" data-testid="invites-empty">
                Ingen ventende invitasjoner.
              </p>
            ) : (
              <ul className="invite-list" data-testid="invites-list">
                {pendingInvites.map((invite) => {
                  const busy = busyInviteId === invite.id;
                  return (
                    <li
                      key={invite.id}
                      className="invite-row card card-pad"
                      data-testid={`invite-row-${invite.id}`}
                    >
                      <div className="member-body">
                        <div className="member-name" data-testid={`invite-email-${invite.id}`}>
                          {invite.email}
                        </div>
                        <div className="member-email" data-testid={`invite-created-${invite.id}`}>
                          Invitert {formatDateTime(invite.createdAt)}
                        </div>
                      </div>
                      <div className="member-actions">
                        <span className="chip" data-testid={`invite-role-${invite.id}`}>
                          {ROLE_LABEL[invite.role]}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger"
                          data-testid={`invite-cancel-${invite.id}`}
                          disabled={busy}
                          onClick={() => handleCancelInvite(invite.id)}
                        >
                          Avbryt
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}
